import { useCallback, useEffect, useRef, useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";

export type TChapterMemoStatus = {
  id: number;
  hasActiveMemo: boolean;
  chapterName: string;
};

type TChapterMemoStatusResponse = {
  chapters: TChapterMemoStatus[];
};

export function useChapterMemoPolling(projectId: string, enabled = true, pollingInterval = 3000) {
  const { setChapters, getChapters, clearChapters } = useChapterMemoStore();
  const chapters = getChapters(projectId);
  const [isPolling, setIsPolling] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousChapterCountRef = useRef<number>(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const poll = useCallback(async () => {
    if (!projectId || !enabled) {
      stopPolling();
      return;
    }

    try {
      const response = await $api.get<TChapterMemoStatusResponse>(`/projects/${projectId}/chapters-memo-status`);

      if (!enabledRef.current) return;

      const newChapters = response.data.chapters;
      setChapters(projectId, newChapters);

      const completedCount = newChapters.filter((ch) => ch.hasActiveMemo).length;

      if (completedCount > previousChapterCountRef.current) {
        console.log(`[CHAPTER_MEMO_POLLING] ${completedCount}/${newChapters.length} chapters completed`);
      }
      previousChapterCountRef.current = completedCount;

      setIsPolling(true);
      pollingTimeoutRef.current = setTimeout(poll, pollingInterval);
    } catch (error) {
      console.error("Error polling chapter memo status:", error);
      stopPolling();
    }
  }, [projectId, enabled, pollingInterval, stopPolling, setChapters]);

  useEffect(() => {
    if (enabled && projectId) {
      poll();
    }

    return () => {
      stopPolling();
    };
  }, [projectId, enabled, poll, stopPolling]);

  useEffect(() => {
    if (!enabled && projectId) {
      clearChapters(projectId);
      previousChapterCountRef.current = 0;
    }
  }, [enabled, projectId, clearChapters]);

  return {
    chapters: enabled ? chapters : [],
    isPolling,
    stopPolling
  };
}
